import {
    Component, inject, signal, OnInit, CUSTOM_ELEMENTS_SCHEMA
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { BusinessService } from '../../shared/services/business.service';
import { BusinessContextService } from '../../core/services/business-context.service';
import { AppButton } from '../../shared/components/app-button/app-button';
import { AppInput } from '../../shared/components/app-input/app-input';
import { Business } from '../../shared/models';
import { LoadingService } from '../../core/services/loading.service';
import { ToastService } from '../../core/services/toast.service';
import { NavigationService } from '../../core/services/navigation.service';
import { AppSectionHeader } from "../../shared/components/app-section-header/app-section-header";

@Component({
    selector: 'app-my-business',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, AppButton, AppInput, AppSectionHeader],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    templateUrl: './my-business.html',
    styleUrl: './my-business.css'
})
export class MyBusiness implements OnInit {
    private fb = inject(FormBuilder);
    private router = inject(Router);
    public bizCtx = inject(BusinessContextService);
    private bizService = inject(BusinessService);
    private loadingService = inject(LoadingService);
    private nav = inject(NavigationService);
    private toast = inject(ToastService);

    form = this.fb.group({
        // Campos Bloqueados (Lectura)
        taxId: [{ value: '', disabled: true }],
        name: [{ value: '', disabled: true }],
        establishmentCode: [{ value: '', disabled: true }],
        emissionPointCode: [{ value: '', disabled: true }],
        environment: [{ value: '', disabled: true }],
        invoiceApiKey: [{ value: '', disabled: true }],
        obligadoContabilidad: [{ value: false, disabled: true }],
        isSpecialTaxpayer: [{ value: false, disabled: true }],
        specialTaxpayerResolution: [{ value: '', disabled: true }],
        regimenType: [{ value: '', disabled: true }],

        // Campos Editables
        commercialName: ['', [Validators.required]],
        address: ['', [Validators.required]],
        establishmentAddress: [''],
        phone: ['', [Validators.required]],
        email: ['', [Validators.required, Validators.email]],

        // Invoicing Files (se manejan por separado pero incluimos password)
        signaturePassword: [''],
        hasSignature: [false]
    });

    currentBusiness = signal<Business | null>(null);
    saving = signal(false);
    error = signal('');

    // File handlers
    private logoFile: File | null = null;
    private signatureFile: File | null = null;
    logoPreview = signal<string | null>(null);
    signatureFileName = signal<string | null>(null);

    ngOnInit() {
        const activeId = this.bizCtx.business();
        if (activeId) {
            this.loadBusiness(activeId);
        } else {
            this.router.navigate(['/app/select-business']);
        }
    }

    getLogoUrl(path?: string): string | null {
        if (!path) return null;
        if (path.startsWith('http') || path.startsWith('data:')) return path;

        const baseUrl = environment.apiUrl.replace('/api/v1', '').replace(/\/$/, '');
        const cleanPath = path.startsWith('/') ? path.slice(1) : path;
        return `${baseUrl}/${cleanPath}`;
    }

    loadBusiness(id: string) {
        this.loadingService.showLoading('Cargando Perfil', 'Obteniendo datos de su negocio...');
        this.bizService.getById(id).subscribe({
            next: (resp: any) => {
                const biz = resp.data || resp;
                this.currentBusiness.set(biz);
                this.form.patchValue({
                    ...biz,
                    regimenType: Array.isArray(biz.regimenType) ? biz.regimenType[0] : biz.regimenType
                });
                this.loadingService.hideLoading();
            },
            error: (err: any) => {
                this.loadingService.hideLoading();
                this.error.set('No se pudo cargar el perfil del negocio.');
            }
        });
    }

    onFileSelected(event: any, field: string) {
        const file = event.target.files[0];
        if (!file) return;

        // Validar tamaño (2 MB máx) para imágenes (logo)
        if (field === 'logo') {
            const maxSize = 2 * 1024 * 1024;
            if (file.size > maxSize) {
                this.toast.show('Archivo muy pesado', 'La imagen debe ser menor a 2MB', 'error');
                event.target.value = ''; // Limpiar el input
                return;
            }

            this.logoFile = file;
            const reader = new FileReader();
            reader.onload = () => {
                this.logoPreview.set(reader.result as string);
            };
            reader.readAsDataURL(file);
        } else {
            this.signatureFile = file;
            this.signatureFileName.set(file.name);
        }
    }

    clearLogoSelection() {
        this.logoFile = null;
        this.logoPreview.set(null);
    }

    clearSignatureSelection() {
        this.signatureFile = null;
        this.signatureFileName.set(null);
        this.form.get('signaturePassword')?.reset();
    }

    goBack() {
        this.nav.goBack();
    }

    save() {
        if (this.form.invalid) return;

        const id = this.bizCtx.business();
        if (!id) return;

        this.saving.set(true);
        this.error.set('');

        const rawData: any = this.form.getRawValue();

        // Consolidamos todo en un único payload (incluyendo archivos)
        const payload = {
            commercialName: rawData.commercialName,
            address: rawData.address,
            establishmentAddress: rawData.establishmentAddress,
            phone: rawData.phone,
            email: rawData.email,
            logo: this.logoFile || undefined,
            signature: this.signatureFile || undefined,
            signaturePassword: rawData.signaturePassword || undefined
        };

        this.bizService.update(id, payload as any).subscribe({
            next: (resp) => {
                this.saving.set(false);

                // Actualizamos el estado local
                const updatedBiz = resp;
                this.currentBusiness.set(updatedBiz);

                // Limpiamos selecciones temporales de archivos
                this.logoFile = null;
                this.signatureFile = null;
                this.signatureFileName.set(null);
                this.form.get('signaturePassword')?.reset();

                this.toast.show('Perfil Actualizado', 'Los cambios se guardaron correctamente', 'success');
            },
            error: (err: any) => {
                this.saving.set(false);
                this.error.set(err?.error?.message || 'Error al actualizar el perfil.');
                this.toast.show('Error', 'No se pudieron guardar los cambios', 'error');
            }
        });
    }
}
